// The 4 Scene Controller (DeviceID 101) on screen — both shells.
//
// The panel has four buttons and no outputs; button N runs scene N of the room
// it is in. So its "controls" are the room's first four scenes: each button is
// drawn with the scene's name (or "not set"), a tap runs it through the same
// server endpoint a physical press uses (core/scenes/sceneController), and
// under the buttons every scene's step list is shown — device, unique number,
// command — so what a press will do is never a mystery.
//
// Programming happens on the room's own Scenes screen (`/room/:roomId/scenes`,
// docs/07-scenes.md): every device of the room with its per-output actions —
// `1-1`, `OPENCURTAIN`, `1-60` … — unlimited steps, stored on the Wall tables.
// The owner gets a button straight to it. Buttons follow the room's scene
// order, so reordering scenes there is how a scene is moved to another button.
//
// A physical press echoes `Scene N` on `<uno>/client`; that button flashes so
// the phone in hand confirms the wall did what it should.
//
// ── `bench` (the MQTT consoles) ────────────────────────────────────────────
// A panel on the bench is in no room (RoomId 0), so it has no scenes and every
// button was disabled — a developer testing the hardware had four dead keys.
// In bench mode the four buttons are always live and a press PUBLISHES
// `Scene N` to the panel — `[uno]Scene N#userId` on `<uno>/device`, the same
// token the panel itself reports on a touch — so the developer sees the
// command go out and whatever the firmware answers come back in the Filtered
// pane. If the button also has a scene, that scene runs as well. Room cards
// and device screens never do this: there the buttons are scenes, nothing else.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, Chip, CircularProgress, Collapse, Divider, IconButton, Snackbar, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useApp } from '../../core/state/AppContext';
import { getDevicesByRoom } from '../../core/api/api';
import { resolveSceneOwner } from '../../core/scenes/sceneOwner';
import { fetchRoomScenes } from '../desktop/pages/scenes/scenesApi';
import {
  SCENE_BUTTONS, parseScenePress, runSceneEverywhere,
} from '../../core/scenes/sceneController';
import { subscribeTopic, unsubscribeTopic, addMessageListener, parseStatus } from '../../core/mqtt/mqttClient';
import { publishCommand } from '../../core/utils/deviceController';
import { withoutHubs } from '../../core/devices/hubDevice';

const asArray = (r) => (Array.isArray(r && r.data) ? r.data : []);

/** The four buttons plus the step lists. `compact` = room-grid card; `bench` = see above. */
export function SceneControllerBody({ device, compact = false, bench = false }) {
  const navigate = useNavigate();
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const roomId = device.RoomId ?? device.RoomID ?? null;
  const locationId = device.LocationID ?? device.LocationId ?? null;

  const [ownerId, setOwnerId] = useState(null);
  const [scenes, setScenes] = useState(null); // null = loading
  const [names, setNames] = useState({}); // uno -> device name
  const [runningId, setRunningId] = useState(null);
  const [flash, setFlash] = useState(null); // scene number just pressed on the wall
  const [open, setOpen] = useState(!compact);
  const [toast, setToast] = useState('');

  // Scenes are stored under the location owner; see docs/07-scenes.md.
  useEffect(() => {
    let alive = true;
    setScenes(null);
    if (!roomId) { setScenes([]); return undefined; }
    (async () => {
      const owner = await resolveSceneOwner(userId, locationId);
      const [list, devs] = await Promise.all([
        fetchRoomScenes(owner, roomId).catch(() => []),
        getDevicesByRoom(userId, roomId, locationId).then(asArray).then(withoutHubs).catch(() => []),
      ]);
      if (!alive) return;
      setOwnerId(owner);
      setScenes(list);
      const map = {};
      devs.forEach((d) => { if (d.UniqueNumber) map[String(d.UniqueNumber)] = d.DeviceName || ''; });
      setNames(map);
    })();
    return () => { alive = false; };
  }, [userId, roomId, locationId]);

  // A physical press: the panel echoes "Scene N" on its client topic.
  useEffect(() => {
    if (!uno) return undefined;
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const n = parseScenePress(parseStatus(String(payload)));
      if (n) { setFlash(n); setTimeout(() => setFlash((f) => (f === n ? null : f)), 1500); }
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uno]);

  const canEdit = ownerId != null && Number(userId) === Number(ownerId);
  const buttons = useMemo(() => Array.from({ length: SCENE_BUTTONS }, (_, i) => ({
    n: i + 1, scene: scenes ? scenes[i] || null : null,
  })), [scenes]);

  const run = async (scene) => {
    if (!scene || runningId) return;
    setRunningId(scene.id);
    try {
      const r = await runSceneEverywhere(userId, scene);
      setToast(r && r.ok === false ? `"${scene.name}": ${r.error || 'not run'}` : `Ran "${scene.name}"`);
    } finally {
      setRunningId(null);
    }
  };

  // Bench: the press goes to the panel first, then the scene (if any).
  const press = (n, scene) => {
    if (!bench) { run(scene); return; }
    publishCommand(uno, `Scene ${n}`, userId);
    if (scene) run(scene);
    else setToast(`Sent "Scene ${n}" to ${uno}`);
  };

  const openEditor = () => navigate(`/room/${roomId}/scenes`, {
    state: { roomName: device.RoomName || 'Room', locationId },
  });

  return (
    <Box sx={{ p: compact ? 1 : 2 }}>
      {/* The four buttons, 2×2 like the panel itself. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: compact ? 1 : 1.5 }}>
        {buttons.map(({ n, scene }) => {
          const busy = scene && runningId === scene.id;
          const lit = flash === n;
          return (
            <Button
              key={n}
              variant={scene ? 'contained' : 'outlined'}
              disabled={bench ? (!!runningId && !busy) : (!scene || (!!runningId && !busy))}
              onClick={() => press(n, scene)}
              sx={{
                minHeight: compact ? 56 : 84,
                flexDirection: 'column',
                textTransform: 'none',
                lineHeight: 1.2,
                borderRadius: 2,
                bgcolor: scene ? (lit ? 'secondary.main' : scene.color) : undefined,
                color: scene ? (lit ? 'secondary.contrastText' : scene.textColor) : undefined,
                transition: 'background-color 200ms',
                '&:hover': { bgcolor: scene ? scene.color : undefined, filter: 'brightness(1.1)' },
              }}
            >
              <Typography sx={{ fontSize: 11, opacity: 0.75 }}>Scene {n}</Typography>
              {busy ? <CircularProgress size={18} color="inherit" /> : (
                <Typography sx={{ fontWeight: 700, fontSize: compact ? 13 : 15 }} noWrap>
                  {scene ? scene.name : scenes === null ? '…' : 'not set'}
                </Typography>
              )}
            </Button>
          );
        })}
      </Box>

      {/* What each button will do. */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
          {scenes === null ? 'Loading scenes…'
            : !roomId ? (bench
              ? 'Not in a room, so no scenes — a press sends "Scene N" to the panel.'
              : 'Not in a room — map this panel to a room to give it scenes.')
              : `${Math.min(scenes.length, SCENE_BUTTONS)} of ${SCENE_BUTTONS} buttons set`}
        </Typography>
        {canEdit && roomId && (
          <Button size="small" startIcon={<EditIcon />} onClick={openEditor} sx={{ textTransform: 'none' }}>
            {compact ? 'Edit' : 'Program scenes'}
          </Button>
        )}
        {scenes && scenes.length > 0 && (
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="steps">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      <Collapse in={open && !!scenes && scenes.length > 0}>
        {buttons.filter((b) => b.scene).map(({ n, scene }) => (
          <Box key={scene.id} sx={{ mt: 1 }}>
            <Divider sx={{ mb: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label={`Scene ${n}`} />
              <Typography sx={{ fontWeight: 600, flex: 1 }} noWrap>{scene.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {scene.steps.length} {scene.steps.length === 1 ? 'command' : 'commands'}
              </Typography>
              <IconButton size="small" onClick={() => run(scene)} disabled={!!runningId} aria-label="run">
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Box>
            {scene.steps.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No commands — nothing happens on this button.</Typography>
            ) : (
              <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, mt: 0.25 }}>
                <tbody>
                  {scene.steps.map((s, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={i}>
                      <Box component="td" sx={{ py: 0.25, pr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                        {names[String(s.uniqueNumber)] || '—'}
                      </Box>
                      <Box component="td" sx={{ py: 0.25, pr: 1, fontFamily: 'monospace', color: 'text.secondary' }}>
                        {s.uniqueNumber}
                      </Box>
                      <Box component="td" sx={{ py: 0.25, fontFamily: 'monospace', textAlign: 'right' }}>
                        {s.message}
                      </Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
          </Box>
        ))}
      </Collapse>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}

/** Room-grid card. */
export default function SceneControllerCard({ device, subtitle, header }) {
  return (
    <Card sx={{ borderRadius: 3 }}>
      {header}
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>{subtitle}</Typography>
      )}
      <SceneControllerBody device={device} compact />
    </Card>
  );
}
